import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Divider from 'LuxKit/Divider'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import Group from 'components/utils/Group'
import { DMY_CASUAL_FORMAT, SHORT_TIME_HOUR_FORMAT_AM_PM } from 'constants/dateFormats'
import { take } from 'lib/array/arrayUtils'
import moment from 'moment'
import { rem } from 'polished'
import React, { useCallback, useMemo, useState } from 'react'
import Image from 'components/Common/Image/Image'
import styled from 'styled-components'
import StructureLabel from 'LuxKit/Label/StructureLabel'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import TextLink from 'LuxKit/TextLink'
import Expander from 'components/utils/Expander'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import switchFunc from 'lib/function/switchFunc'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import LineUtensilsAltIcon from 'LuxKit/Icons/line/LineUtensilsAltIcon'
import LineSeatIcon from 'LuxKit/Icons/line/LineSeatIcon'
import LineBagIcon from 'LuxKit/Icons/line/LineBagIcon'
import LineReturnFlightAltIcon from 'LuxKit/Icons/line/LineReturnFlightAltIcon'
import { isSpoofed } from 'selectors/featuresSelectors'
import { connect } from 'react-redux'
import LineShieldCheckIcon from 'LuxKit/Icons/line/LineShieldCheckIcon'
import { useIntl } from 'react-intl'
import LineCancelIcon from 'LuxKit/Icons/line/LineCancelIcon'
import LineRefundIcon from 'LuxKit/Icons/line/LineRefundIcon'

const featureToIconMap = switchFunc<React.ReactElement, App.FareFamilyFeatureType>({
  meals: <LineUtensilsAltIcon />,
  seat_reservation: <LineSeatIcon />,
  checked_baggage: <LineBagIcon />,
  changes: <LineReturnFlightAltIcon />,
  carry_on_bag: <LineBagIcon />,
  cancellation: <LineCancelIcon />,
  refunds: <LineRefundIcon />,
  insurance: <LineShieldCheckIcon />,
}, <LineCheckIcon />)

const Details = styled.div`
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  border-radius: ${props => props.theme.borderRadius.S};
`

// Need the margin internal to the slide down
const FlightMoreDetails = styled(VerticalSpacer)`
  margin-top: ${rem(16)};
`

const FlightItemLocation = styled.div`
  padding: ${rem(12)} 0;
`

const FlightItem = styled.div`
  display: grid;
  grid-template-columns: ${rem(72)} 1px 1fr;
  padding: 0 ${rem(16)};
  gap: ${rem(16)};
`

const ItemLine = styled(Divider)`
  position: relative;
  align-self: stretch;
  height: auto;

  &::before {
    content: '';
    position: absolute;
    background-color: ${props => props.theme.palette.neutral.default.eight};
    top: 50%;
    left: 50%;
    transform: translateY(-50%) translateX(-50%);
    height: ${rem(8)};
    width: ${rem(8)};
    border-radius: ${props => props.theme.borderRadius.round};
    border: 1px solid ${props => props.theme.palette.neutral.default.two};
  }
`

const FlightInfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${rem(4)};
`

const DetailLine = styled(Divider)`
  position: relative;
  align-self: stretch;
  height: auto;
`

const DetailLineIcon = styled(LinePlaneDepartureIcon)`
  position: absolute;
  top: ${rem(22)};
  left: 50%;
  transform: translateY(-50%) translateX(-50%);
  background-color: ${props => props.theme.palette.neutral.default.seven};
`

const FlightDetailItem = styled(FlightItem)`
  background-color: ${props => props.theme.palette.neutral.default.seven};
`

const CarrierLogo = styled(Image)`
  max-width: ${rem(72)};
  width: auto;
`

interface Props {
  journey: App.JourneyV2 | App.Leg
  flight: App.FlightV2 | App.LegFlight
  flightIndex: number
  fareFamily?: App.FlightFareFamily
  inclusions?: App.InclusionFeatures
  addedBaggage?: App.InclusionFeatures
  defaultOpen?: boolean
  enableViewFareRulesButton: boolean
  onShowFareRules: (urls: string, params?: any) => void
  isSpoofed: boolean
  withInsurance?: boolean
}

function generateIncludedInsuranceFeature(group: number, description: string): App.FareFamilyFeature {
  return { status: 'INCLUDED', code: 'insurance', group, category: 'extra', description, type: 'insurance' }
}

function JourneyV2DetailsModalFlight(props: Props) {
  const { journey, flight, flightIndex, fareFamily, inclusions, addedBaggage, defaultOpen = false, enableViewFareRulesButton, onShowFareRules, withInsurance } = props
  const intl = useIntl()
  const [showMore, setShowMore] = useState<boolean>(defaultOpen)

  const showLessLabel = intl.formatMessage({
    id: 'MSK819G+Ov',
    defaultMessage: 'Show less',
    description: 'Link to collapse flight details in the flight search or booking flow',
  })

  const showMoreLabel = intl.formatMessage({
    id: 'H4jaksR6Lh',
    defaultMessage: 'Show more',
    description: 'Link text to expand and show more details about the selected flight itinerary',
  })

  const cancellationProtectionLabel = intl.formatMessage({
    id: 'kb/+/G2IFG',
    defaultMessage: 'Cancellation Protection',
    description: 'Feature label for cancellation protection insurance inclusion',
  })

  const seatSelectionLabel = intl.formatMessage({
    id: 'cIaZ3gNrhR',
    defaultMessage: 'Seat selection',
    description: 'Feature label for seat selection availability on a flight',
  })

  const features = useMemo((): App.InclusionFeatures => {
    if (inclusions) {
      const insuranceFeatures: Array<App.FareFamilyFeature> = withInsurance ? [generateIncludedInsuranceFeature(inclusions.length, cancellationProtectionLabel)] : []
      return [...take(inclusions, 6), ...insuranceFeatures]
    }

    if (fareFamily) {
      const includedFeatures = fareFamily.fareFamily.fareFeatures.filter(feature => feature.status === 'INCLUDED')

      const insuranceFeatures: Array<App.FareFamilyFeature> = withInsurance ? [generateIncludedInsuranceFeature(includedFeatures.length, cancellationProtectionLabel)] : []

      if (addedBaggage) {
        if (addedBaggage?.length > 0 && flight.operatingAirline.code === 'JQ') {
          return [...take(includedFeatures, 6), ...insuranceFeatures, ...addedBaggage]
        }
      }
      return [...take(includedFeatures, 6), ...insuranceFeatures]
    }

    // not a fare family, so we have to build the features list ourselves
    const flightFeatures: App.InclusionFeatures = []
    if ('includedCarryOnBaggage' in flight && flight.includedCarryOnBaggage) {
      flightFeatures.push({
        description: flight.includedCarryOnBaggage.description,
        type: 'carry_on_bag',
      })
    }

    if ('includedCheckedBaggage' in flight && flight.includedCheckedBaggage) {
      flightFeatures.push({
        description: flight.includedCheckedBaggage.description,
        type: 'checked_baggage',
      })
    } else if (addedBaggage) {
      if (addedBaggage?.length > 0 && flight.operatingAirline.code === 'JQ') {
        flightFeatures.push(...addedBaggage)
      }
    }

    if ('seatsSelectionAvailable' in flight && flight.seatsSelectionAvailable) {
      flightFeatures.push({
        description: seatSelectionLabel,
        type: 'seat_reservation',
      })
    }

    if (withInsurance) {
      flightFeatures.push(generateIncludedInsuranceFeature(flightFeatures.length, cancellationProtectionLabel))
    }

    return flightFeatures
  }, [inclusions, fareFamily, flight, addedBaggage, withInsurance, cancellationProtectionLabel, seatSelectionLabel])

  const openFareRulesModal = useCallback(() => {
    const hasMoreThanOneLink = journey.links.length > 1
    const links = journey.links
    const firstLink = journey.links[0]?.value
    const linkByFlightIndex = links[flightIndex]?.value
    onShowFareRules(hasMoreThanOneLink ? linkByFlightIndex : firstLink)
  }, [journey.links, flightIndex, onShowFareRules])

  const fareFamilyDescription = useMemo(() => {
    return fareFamily?.fareFamily.description ?? (journey as App.JourneyV2)?.fareFamily?.description ?? flight.fareClass
  }, [fareFamily?.fareFamily.description, flight.fareClass, journey])

  const bookingClass = useMemo(() => {
    return fareFamily?.bookingClass ?? flight.bookingClass
  }, [fareFamily?.bookingClass, flight.bookingClass])

  const flightTime = 'duration' in flight ? flight.duration : flight.flightTime

  const departureDateTime = `${flight.departingDate} ${flight.departingTime}`
  const arrivalDateTime = `${flight.arrivalDate} ${flight.arrivalTime}`

  const flightNumberLabel = intl.formatMessage(
    {
      id: 'ipiNR7NAGA',
      defaultMessage: '{airlineCode}-{flightNumber}',
      description: 'Flight number with airline code in flight details modal',
    },
    { airlineCode: flight.operatingAirline.code, flightNumber: flight.flightNumber },
  )

  const fareClassDisplayText = bookingClass
    ? intl.formatMessage(
        {
          id: '6RNE49yGUQ',
          defaultMessage: '{description} ({bookingClass})',
          description: 'Fare class description with booking class code in flight details modal',
        },
        { description: fareFamilyDescription, bookingClass },
      )
    : fareFamilyDescription

  return <Details>
    <FlightItem>
      <FlightItemLocation>
        <BodyText align="end" variant="small" weight="bold">
          {moment(departureDateTime).format(SHORT_TIME_HOUR_FORMAT_AM_PM)}
        </BodyText>
        <Caption align="end" variant="smallalt" tabletVariant="medium" colour="neutral-two">
          {moment(flight.departingDate).format(DMY_CASUAL_FORMAT)}
        </Caption>
      </FlightItemLocation>
      <ItemLine kind="primary" direction="vertical" />
      <FlightItemLocation>
        <BodyText variant="small" weight="bold">{flight.departureAirport}</BodyText>
        <Caption variant="smallalt" tabletVariant="medium" colour="neutral-two">
          {intl.formatMessage(
            {
              id: 'NbPpSpzhQU',
              defaultMessage: '{airportName} ({airportCode})',
              description: 'Departure airport name with code in flight journey detail modal',
            },
            { airportName: flight.departureAirportName, airportCode: flight.departureAirport },
          )}
        </Caption>
      </FlightItemLocation>
    </FlightItem>
    <FlightDetailItem>
      <FlightItemLocation>
        <CarrierLogo
          width={72}
          image={{
            url: flight.operatingAirline.logoUrl,
            title: flight.operatingAirline.name,
          }}
        />
      </FlightItemLocation>
      <DetailLine kind="primary" direction="vertical">
        <DetailLineIcon size="XS" />
      </DetailLine>
      <FlightItemLocation>
        <Group direction="horizontal" horizontalAlign="space-between">
          <LabelGroup>
            <StructureLabel variant="light">{flight.operatingAirline.name}</StructureLabel>
          </LabelGroup>
          <TextLink
            size="caption"
            weight="regular"
            wrap="no-wrap"
            onClick={() => setShowMore(val => !val)}
          >
            {showMore ? showLessLabel : showMoreLabel}
          </TextLink>
        </Group>
        <Expander expanded={showMore}>
          <FlightMoreDetails gap={16}>
            <FlightInfoGrid>
              <Caption variant="large" weight="bold" style={{ gridColumn: 'span 2' }}>
                {intl.formatMessage({
                  id: 'a/IX+l7dy0',
                  defaultMessage: 'Flight information',
                  description: 'Section title for flight information in details modal',
                })}
              </Caption>
              <Caption variant="large">
                {intl.formatMessage({
                  id: 'TICuLZcfyZ',
                  defaultMessage: 'Flight number',
                  description: 'Label for flight number in details modal in the flights flow',
                })}
              </Caption><Caption variant="large" align="end">{flightNumberLabel}</Caption>
              {
                (!!flightTime.hours || !!flightTime.minutes) &&
                <>
                  <Caption variant="large">
                    {intl.formatMessage({
                      id: 'rk+S5pNV2A',
                      defaultMessage: 'Duration',
                      description: 'Label for flight duration in details modal in the flights flow',
                    })}
                  </Caption>
                  <Caption variant="large" align="end">
                    {
                      // eslint-disable-next-line formatjs/no-multiple-plurals
                      intl.formatMessage({
                        id: '9H6uyzFlIN',
                        defaultMessage: '{hours, plural, one {# hour} other {# hours}}, {minutes, plural, one {# minute} other {# minutes}}',
                        description: 'Full flight duration display showing hours and minutes',
                      }, { hours: flightTime.hours, minutes: flightTime.minutes })
                    }
                  </Caption>
                </>
              }
              <Caption variant="large">
                {intl.formatMessage({
                  id: 'lgJWrWwCxD',
                  defaultMessage: 'Aircraft',
                  description: 'Form field label for aircraft type in flight details modal',
                })}
              </Caption><Caption variant="large" align="end">{flight.aircraftType}</Caption>
            </FlightInfoGrid>
            <VerticalSpacer gap={4}>
              <FlightInfoGrid>
                <Caption variant="large" weight="bold">
                  {intl.formatMessage({
                    id: 'xSt6OEsggh',
                    defaultMessage: 'Fare class',
                    description: 'Label for fare class in flight details modal in the flights flow',
                  })}
                </Caption>
                {enableViewFareRulesButton && <Caption variant="large" align="end">
                  <TextLink
                    size="caption"
                    weight="regular"
                    onClick={() => openFareRulesModal()}
                  >
                    {intl.formatMessage({
                      id: 'sGZSYnOda2',
                      defaultMessage: 'View fare rules',
                      description: 'Link text to view fare rules in flight details modal',
                    })}
                  </TextLink>
                </Caption>}
              </FlightInfoGrid>
              <Caption variant="large">{fareClassDisplayText}</Caption>
            </VerticalSpacer>
            {features.length > 0 && <VerticalSpacer gap={4}>
              <Caption variant="large" weight="bold">
                {intl.formatMessage({
                  id: '55fWHISWA/',
                  defaultMessage: 'Inclusions',
                  description: 'Section title for included features in flight details modal',
                })}
              </Caption>
              {features.map(feature =>
                <CaptionTextBlock
                  variant="medium"
                  colour="neutral-two"
                  key={feature.description}
                  startIcon={featureToIconMap(feature.type)}
                >
                  {feature.description}
                </CaptionTextBlock>)}
            </VerticalSpacer>}
          </FlightMoreDetails>
        </Expander>
      </FlightItemLocation>
    </FlightDetailItem>
    <FlightItem>
      <FlightItemLocation>
        <BodyText align="end" variant="small" weight="bold">
          {moment(arrivalDateTime).format(SHORT_TIME_HOUR_FORMAT_AM_PM)}
        </BodyText>
        <Caption align="end" variant="smallalt" tabletVariant="medium" colour="neutral-two">{moment(flight.arrivalDate).format(DMY_CASUAL_FORMAT)}</Caption>
      </FlightItemLocation>
      <ItemLine kind="primary" direction="vertical" />
      <FlightItemLocation>
        <BodyText variant="small" weight="bold">{flight.arrivalAirport}</BodyText>
        <Caption variant="smallalt" tabletVariant="medium" colour="neutral-two">
          {intl.formatMessage(
            {
              id: 'zUGB3fA16a',
              defaultMessage: '{airportName} ({airportCode})',
              description: 'Arrival airport name with code in flight journey detail modal',
            },
            { airportName: flight.arrivalAirportName, airportCode: flight.arrivalAirport },
          )}
        </Caption>
      </FlightItemLocation>
    </FlightItem>
  </Details>
}

function mapStateToProps(state: App.State) {
  return {
    isSpoofed: isSpoofed(state),
  }
}
export default connect(mapStateToProps)(JourneyV2DetailsModalFlight)
